'use client';

import * as React from 'react';
import { ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import {
  SiClaude,
  SiGmail,
  SiHubspot,
  SiOpenai,
  SiSalesforce
} from 'react-icons/si';

import { routes } from '@workspace/routes';
import { cn } from '@workspace/ui/lib/utils';

import { CalendlyButton } from '~/components/calendly-button';
import { WARM_PIPELINE_COPY } from '~/lib/landing-content';
import { HeroGeometricBackground } from './hero-geometric-background';

/**
 * Pipeline and mid-funnel landing (Abu, Oct 28 trial). Duplicated from
 * HeroSearch so the original hero stays untouched; the old sections are
 * commented out in app/page.tsx, not deleted.
 *
 * Start free sits at the top for visitors ready to try it. Book a call sits
 * at the bottom for visitors who read down first.
 */
const PIPELINE_HERO = {
  // Warm pipeline for sales teams (Abu, Nov 2). No eyebrow: the backers
  // pill above already carries the credibility line.
  eyebrow: '',
  // "High-intent" dropped from the headline; the sub carries timing (Abu, Nov 2).
  headlineLead: 'Warm pipeline',
  // Second line rotates through who it is for (Abu, Nov 1, after 8090).
  // Founders dropped: the buyer is a sales team (Abu, Nov 1).
  // "Account executives" over "enterprise AEs": AEs selling to multi-site
  // mid-market want warm paths too. "Sales leaders" names the person who
  // signs, since the AE using it often can't (Justin call, Abu, Nov 2).
  headlineAudiences: ['sales teams.', 'account executives.', 'sales leaders.'],
  subheadline: WARM_PIPELINE_COPY.subheadline,
  // Primary path opens Calendly: buyers come in through a call and a pilot,
  // so the demo leads. The line under it says what the call gets you; plain
  // text so there is one thing to click. Start free lives in the nav (Abu, Nov 2).
  cta: 'Book a demo',
  demoNote: 'Book 30 minutes to map warm paths into your top accounts together.'
} as const;

// The three jobs the category covers, in the same order as the headline.
const PIPELINE_JOBS = [
  {
    id: 'build-pipeline',
    title: 'Build pipeline',
    body: 'Find the accounts showing intent, the people to reach, and the reason to write today.'
  },
  {
    id: 'progress-accounts',
    title: 'Progress accounts',
    body: 'Know when someone on an open deal engages a competitor or changes jobs, and act before the next call.'
  },
  {
    id: 'grow-audience',
    title: 'Grow your audience',
    body: 'See who in your market is talking about your problem, and become the name they know.'
  }
] as const;

// Outside-signal difference (vs. call-recorder tools like Cedar), shown as
// deal rows. Accounts and people are illustrative, not customers.
const PIPELINE_SIGNALS = {
  headlineLead: 'Your call recorder hears what buyers say to you.',
  headlineRest: 'Lumnis sees what they do everywhere else.',
  cardTitle: 'This week · 3 accounts need you',
  cardTag: 'Example',
  rows: [
    {
      account: 'Brightline',
      signal: 'Open deal',
      what: 'Their VP of Sales commented on a competitor’s launch post yesterday.',
      next: 'Send your champion the comparison before Thursday’s call.',
      evidence: 'LinkedIn comment · Oct 27',
      when: 'Today'
    },
    {
      account: 'Kestrel',
      signal: 'Champion move',
      what: 'Dana Ortiz, your champion, started as CRO at Halden this week.',
      next: 'Congratulate her and ask who owns the evaluation at Halden.',
      evidence: 'Job change · Oct 26',
      when: 'Today'
    },
    {
      account: 'Halden',
      signal: 'Target account',
      what: 'Posted three RevOps roles that name the problem you solve.',
      next: 'Write to the VP of RevOps with the hiring as your reason.',
      evidence: '3 job posts · Oct 24',
      when: 'This week'
    }
  ]
} as const;

const PIPELINE_BOOK_CALL = {
  // Close on the live deal (Abu, Nov 1).
  headline: 'Bring one live deal.',
  body: 'We’ll show you who’s helping, who’s gone quiet and the best way to bring more people in.',
  // What the 30 days look like, from the Oct 16 GTM deck's closing slide.
  weeks: [
    {
      when: 'Week 1',
      title: 'Agree what matters',
      body: 'Your accounts, your team’s questions and the decisions the research should support.'
    },
    {
      when: 'Week 2',
      title: 'First results',
      body: 'What changed on your live deal, and your ways into it.'
    },
    {
      when: 'Every week after',
      title: 'Tune it together',
      body: 'We adjust what Lumnis looks for from what your team approves and rejects.'
    }
  ],
  cta: 'Book a call',
  // Self-serve next to the call, same as the hero (Abu, Nov 1).
  secondaryCta: 'Start free'
} as const;

// Tools a rep already uses, one quiet row under the button (Abu, Nov 3).
// Only what Lumnis connects to today; Claude and ChatGPT via the MCP server.
type WorksWithTool = {
  label: string;
  Icon?: React.ComponentType<{ className?: string }>;
  image?: string;
};

const WORKS_WITH: readonly WorksWithTool[] = [
  { label: 'Claude', Icon: SiClaude },
  { label: 'ChatGPT', Icon: SiOpenai },
  { label: 'Salesforce', Icon: SiSalesforce },
  { label: 'HubSpot', Icon: SiHubspot },
  { label: 'Attio', image: '/assets/integrations/attio.png' },
  { label: 'Gmail', Icon: SiGmail },
  { label: 'Outlook', image: '/assets/integrations/outlook.png' }
];

function HeroWorksWith(): React.JSX.Element {
  return (
    <div className="mt-9 flex max-w-[760px] flex-col items-center gap-3 md:flex-row md:gap-5">
      <span className="shrink-0 font-mono text-[10.5px] font-medium uppercase tracking-[0.12em] text-[color:var(--ink-4)]">
        Works with
      </span>
      <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5 md:justify-start">
        {WORKS_WITH.map((tool) => (
          <li
            key={tool.label}
            className="flex items-center gap-1.5 text-[14px] font-medium text-[color:var(--ink-3)]"
          >
            {tool.Icon ? (
              <tool.Icon className="size-4 shrink-0" />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={tool.image}
                alt=""
                data-logo-adaptive
                className="size-4 shrink-0 object-contain grayscale"
              />
            )}
            {tool.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PipelineHero(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const [rotations, setRotations] = React.useState(0);
  const audienceIndex = rotations % PIPELINE_HERO.headlineAudiences.length;
  React.useEffect(() => {
    if (shouldReduceMotion) return;
    const timer = window.setInterval(
      () => setRotations((count) => count + 1),
      3200
    );
    return () => window.clearInterval(timer);
  }, [shouldReduceMotion]);
  const payoffAt = PIPELINE_HERO.subheadline.indexOf(' (');
  const subheadlineLead =
    payoffAt === -1
      ? PIPELINE_HERO.subheadline
      : PIPELINE_HERO.subheadline.slice(0, payoffAt);
  // No full stop after the payoff line on the page (Abu, Nov 2).
  const subheadlinePayoff =
    payoffAt === -1
      ? ''
      : PIPELINE_HERO.subheadline.slice(payoffAt + 1).replace(/\.$/, '');
  const headlineLines = React.useMemo(
    () => [
      PIPELINE_HERO.headlineLead,
      `for ${PIPELINE_HERO.headlineAudiences[audienceIndex]}`
    ],
    [audienceIndex]
  );

  return (
    <section className="lumnis-hero relative overflow-hidden bg-[color:var(--paper)]">
      <HeroGeometricBackground />
      <div
        aria-hidden
        className="hero-corner tr"
      />

      <div className="lumnis-hero__content relative w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <div className="flex flex-col items-center text-center">
          {/* Backers live in the logo band under the hero (Abu, Nov 2). */}
          {PIPELINE_HERO.eyebrow && (
            <motion.span
              className="lumnis-hero__eyebrow flex max-w-full items-center gap-3 mb-6 font-mono text-[10.5px] md:text-[11px] tracking-[0.12em] uppercase font-medium text-[color:var(--ink-3)]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.12, duration: 0.5 }}
            >
              <span
                aria-hidden
                className="hidden sm:block h-px w-6 md:w-10 shrink-0 bg-[color:var(--hairline)]"
              />
              <span>{PIPELINE_HERO.eyebrow}</span>
              <span
                aria-hidden
                className="hidden sm:block h-px w-6 md:w-10 shrink-0 bg-[color:var(--hairline)]"
              />
            </motion.span>
          )}

          {/* Solid type everywhere: the dot fill read fainter than the sub
              under it, and its effect only showed on mouse-over (Abu, Nov 2). */}
          <h1 className="lumnis-hero__headline lumnis-hero__headline--stacked relative max-w-full text-[color:var(--ink)]">
            {/* The changing line rises in. */}
            {headlineLines.map((line, index) => (
              <span
                key={line}
                className={cn(
                  'lumnis-hero__headline-line',
                  // The category in brand green; the audience stays ink (Abu, Nov 2).
                  index === 0 && 'text-[color:var(--accent-deep)]',
                  index > 0 && rotations > 0 && 'lumnis-hero__headline-swap'
                )}
              >
                {index > 0 && <span className="sr-only"> </span>}
                {line}
              </span>
            ))}
          </h1>

          <motion.div
            className="lumnis-hero__description mt-6"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            {/* The payoff in parentheses gets its own line on every screen,
                so the promise and the payoff read as two glances (Abu, Nov 2).
                Meta and link previews keep the one-line sentence. */}
            <p className="text-[color:var(--ink)]">
              {subheadlineLead}
              {subheadlinePayoff && (
                <span className="block">{subheadlinePayoff}</span>
              )}
            </p>
          </motion.div>

          <motion.div
            className="lumnis-hero__action mt-7 flex flex-col items-center"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
          >
            <CalendlyButton
              text={PIPELINE_HERO.cta}
              className="marketing-cta marketing-cta--hero"
            />

            <p className="mt-3 text-[14px] text-[color:var(--ink-3)]">
              {PIPELINE_HERO.demoNote}
            </p>

            <HeroWorksWith />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function PipelineMoments(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      id="moments"
      className="relative py-16 md:py-24 bg-[color:var(--paper)]"
    >
      <div className="w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-[1120px] mx-auto">
          {PIPELINE_JOBS.map((m, index) => (
            <motion.li
              key={m.id}
              // Mobile only: the stacked cards get the backers-pill tint so
              // they carry weight without turning the page dark.
              className="flex flex-col rounded-[20px] border border-[color:color-mix(in_srgb,#00C896_35%,transparent)] bg-[color:color-mix(in_srgb,#00C896_6%,var(--paper))] p-6 md:border-[color:var(--hairline)] md:bg-[color:var(--paper)] md:p-7"
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ delay: index * 0.08, duration: 0.45 }}
            >
              <span className="mb-3 md:mb-4 font-mono text-[13px] md:text-[10.5px] tracking-[0.1em] uppercase font-medium text-[color:var(--accent-darker)]">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="text-[28px] font-semibold leading-[1.05] tracking-[-0.025em] md:text-xl md:font-medium md:leading-snug md:tracking-normal text-[color:var(--ink)]">
                {m.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-[color:var(--ink-3)]">
                {m.body}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function PipelineSignals(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="relative py-16 md:py-24 bg-[color:var(--paper)]">
      <div className="w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <h2 className="display-h2 mx-auto mb-10 md:mb-14 max-w-[22ch] text-center text-3xl md:text-5xl [text-wrap:balance]">
          <span className="text-[color:var(--ink-3)]">
            {PIPELINE_SIGNALS.headlineLead}
          </span>{' '}
          <span className="text-[color:var(--ink)]">
            {PIPELINE_SIGNALS.headlineRest}
          </span>
        </h2>

        <motion.div
          className="mx-auto max-w-[920px] overflow-hidden rounded-[20px] border border-[color:var(--hairline)] bg-[color:var(--paper)] shadow-sm"
          initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-[color:var(--hairline)] px-5 py-3.5 md:px-7">
            <span className="font-mono text-[10.5px] md:text-[11px] tracking-[0.1em] uppercase font-medium text-[color:var(--ink-3)]">
              {PIPELINE_SIGNALS.cardTitle}
            </span>
            <span className="font-mono text-[10px] tracking-[0.08em] uppercase text-[color:var(--ink-4)]">
              {PIPELINE_SIGNALS.cardTag}
            </span>
          </div>
          <ul>
            {PIPELINE_SIGNALS.rows.map((row) => (
              <li
                key={row.account}
                className="grid gap-3 border-b border-[color:var(--hairline)] px-5 py-5 last:border-b-0 md:grid-cols-[150px_minmax(0,1fr)_90px] md:gap-6 md:px-7"
              >
                <div className="flex items-center gap-2 md:flex-col md:items-start md:gap-1.5">
                  <span className="text-[15px] font-semibold text-[color:var(--ink)]">
                    {row.account}
                  </span>
                  <span
                    className="inline-flex rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.06em] uppercase"
                    style={{
                      background:
                        'color-mix(in srgb, #00C896 10%, var(--paper))',
                      color: 'var(--accent-darker)'
                    }}
                  >
                    {row.signal}
                  </span>
                </div>
                <div>
                  <p className="text-[15px] leading-relaxed text-[color:var(--ink)]">
                    {row.what}
                  </p>
                  <p className="mt-1.5 flex gap-1.5 text-[14px] leading-relaxed text-[color:var(--ink-3)]">
                    <ArrowRight
                      className="mt-1 size-3.5 shrink-0"
                      aria-hidden="true"
                    />
                    {row.next}
                  </p>
                  <p className="mt-2 font-mono text-[10.5px] tracking-[0.04em] text-[color:var(--ink-4)]">
                    {row.evidence}
                  </p>
                </div>
                <span className="text-[13px] font-medium text-[color:var(--ink-3)] md:text-right">
                  {row.when}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

export function PipelineBookCall(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="relative py-16 md:py-24 bg-[color:var(--paper)]">
      <div className="w-full relative px-6 md:px-8 lg:px-12 xl:px-16 mx-auto max-w-[1320px]">
        <motion.div
          className="relative overflow-hidden rounded-[28px] bg-[#0A0B0A] text-[#F4F4F0] px-6 py-14 md:px-12 md:py-20 text-center"
          initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(60% 80% at 50% 100%, color-mix(in srgb, #00C896 55%, transparent), transparent 70%)',
              opacity: 0.9
            }}
          />
          <div className="relative flex flex-col items-center">
            <h2 className="display-h2 text-3xl md:text-4xl lg:text-5xl mb-4 [text-wrap:balance]">
              {PIPELINE_BOOK_CALL.headline}
            </h2>
            <p className="text-lg md:text-xl text-[#B7BBB4] max-w-[520px] leading-relaxed mb-8">
              {PIPELINE_BOOK_CALL.body}
            </p>
            <ol className="mb-10 grid w-full max-w-[880px] list-none gap-3 p-0 text-center md:grid-cols-3 md:text-left">
              {PIPELINE_BOOK_CALL.weeks.map((week) => (
                <li
                  key={week.when}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#00C896]">
                    {week.when}
                  </p>
                  <p className="mt-1.5 text-[16px] font-medium text-[#F4F4F0]">
                    {week.title}
                  </p>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-[#B7BBB4]">
                    {week.body}
                  </p>
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <CalendlyButton
                text={PIPELINE_BOOK_CALL.cta}
                className="bg-teal-500 hover:bg-[#00E5AA] text-[#001A12] px-7 h-[52px] text-[15.5px] font-medium rounded-full shadow-md shadow-teal-900/20 hover:-translate-y-0.5 active:scale-[0.98] transition-all"
              />
              <a
                href={routes.dashboard.auth.SignUp}
                className="inline-flex h-[52px] items-center rounded-full border border-white/20 px-7 text-[15.5px] font-medium text-[#F4F4F0] transition-colors hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00C896]"
              >
                {PIPELINE_BOOK_CALL.secondaryCta}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
